import { Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import KcAdminClient from '@keycloak/keycloak-admin-client';
import { environment } from '../../../environments/environment';
import { SecurePersistStateService } from '../storage/local-storage.service';
import { UserProfileService } from '../user/user-profile.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { GrantTypes } from '@keycloak/keycloak-admin-client/lib/utils/auth';
import { SettingsService } from '../settings/settings.service';
import { DEV_ROLES, makeDevJwt } from './dev-auth-stub';
import { jwtDecode } from 'jwt-decode';
import {
  RETURN_URL_PARAM,
  SIGN_IN_REASON_PARAM,
  SignInReason,
  returnUrlParam,
} from '../routing/return-url';

/**
 * Treat a token as expired this long before its `exp`, so a request sent on
 * the boundary does not fail halfway through a page.
 */
const EXPIRY_SLACK_MS = 15_000;

/**
 * Stored next to the token (and cleared with it): how far this device's clock
 * runs ahead of Keycloak's, so expiry is judged in the server's time.
 */
const CLOCK_SKEW_KEY = 'clockSkewMs';

@Injectable({
  providedIn: 'root',
})
export class KeycloakAdminService {
  private kcAdminClient: KcAdminClient;
  storageService = inject(SecurePersistStateService);
  userProfileService = inject(UserProfileService);
  settingsService = inject(SettingsService);
  router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    // Initialize the Keycloak Admin Client with the base URL and realm
    this.kcAdminClient = new KcAdminClient({
      baseUrl: environment.keycloakUrl,
      realmName: environment.keyCloak.realmName,
    });

    this.seedDevSession();
  }

  /**
   * DEV ONLY: when the `mock` configuration sets `devBypassAuth`, plant a stub
   * session so the route guards pass and the UI renders without a login.
   * Browser-only — localStorage/SecureLS are unavailable during SSR.
   */
  private seedDevSession(): void {
    const { devBypassAuth } = environment as { devBypassAuth?: boolean };
    if (!devBypassAuth || !isPlatformBrowser(this.platformId)) {
      return;
    }

    // getData() throws when no encrypted state exists yet (fresh browser),
    // so probe it separately rather than letting it skip the seed below.
    let existing: string | undefined;
    try {
      existing = this.storageService.getData('accessToken');
    } catch {
      existing = undefined;
    }

    try {
      const token = existing || makeDevJwt();
      this.storageService.saveSecureData('accessToken', token);
      this.userProfileService.updateUser(token);
      this.settingsService.seedDevRoles(DEV_ROLES);
    } catch {
      // Never let the stub break app startup.
    }
  }

  /**
   * Authenticate with Keycloak using username and password.
   * @param username The Keycloak username.
   * @param password The Keycloak password.
   * @returns A promise that resolves when authentication is complete.
   */
  async authenticate(
    username: string,
    password: string,
    totp?: string
  ): Promise<void> {
    try {
      await this.kcAdminClient.auth({
        username: username,
        password: password,
        clientSecret: environment.keyCloak.clientSecret,
        grantType: environment.keyCloak.grantType as GrantTypes,
        clientId: environment.keyCloak.clientId,
        scopes: environment.keyCloak.scopes,
      });
    } catch (error: any) {
      // Log the full error for debugging

      // Get status code from error
      const statusCode = error?.response?.status || error?.status;

      // Extract error message from server response if available
      let errorMessage = 'Something went wrong. Please try again.';

      // Check various error response structures
      const responseData = error?.response?.data || error?.data;

      // For 400-level errors, prioritize error_description from response
      if (statusCode >= 400 && statusCode < 500) {
        if (responseData?.error_description) {
          // Keycloak specific error description
          errorMessage = responseData.error_description;
        } else if (responseData?.message) {
          // Generic message field
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          // Keycloak error field
          errorMessage = responseData.error;
        } else if (typeof responseData === 'string') {
          // Sometimes the response data is a string
          errorMessage = responseData;
        } else {
          // Fallback to generic 400-level error messages
          switch (statusCode) {
            case 400:
              errorMessage = 'Invalid request. Please check your credentials.';
              break;
            case 401:
              errorMessage = 'Invalid username or password. Please try again.';
              break;
            case 403:
              errorMessage =
                'Access forbidden. You do not have permission to login.';
              break;
            case 404:
              errorMessage =
                'Authentication service not found. Please contact support.';
              break;
            case 429:
              errorMessage = 'Too many login attempts. Please try again later.';
              break;
            default:
              errorMessage = 'Invalid credentials. Please check your input.';
          }
        }
      } else if (statusCode >= 500) {
        // For 500-level errors, show 'Network response was not OK' or generic server error
        if (error?.message === 'Network response was not OK') {
          errorMessage = 'Network response was not OK';
        } else {
          switch (statusCode) {
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
            case 502:
            case 503:
            case 504:
              errorMessage =
                'Service temporarily unavailable. Please try again later.';
              break;
            default:
              errorMessage = 'Server error. Please try again later.';
          }
        }
      } else if (responseData?.error_description) {
        // For other status codes, check for error_description
        errorMessage = responseData.error_description;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (responseData?.error) {
        errorMessage = responseData.error;
      } else if (typeof responseData === 'string') {
        errorMessage = responseData;
      }

      // Attach user-friendly message to the original error object
      error.message = errorMessage;
      error.userMessage = errorMessage;
      error.statusCode = statusCode;
      throw error;
    }

    this.storageService.saveSecureData(
      'accessToken',
      this.kcAdminClient.getAccessToken()
    );
    this.recordClockSkew(this.kcAdminClient.accessToken);
    this.userProfileService.updateUser(this.kcAdminClient.accessToken);
    this.settingsService.fetchUserProfile();
    this.settingsService.fetchUserInstitution();
  }

  /**
   * Fetch a list of users from Keycloak.
   * @param first The starting index of users to fetch.
   * @param max The maximum number of users to fetch.
   * @returns A promise that resolves to the list of users.
   */
  async getUsers(first: number = 0, max: number = 10): Promise<any> {
    return this.kcAdminClient.users.find({ first, max });
  }

  /**
   * Find users by attributes in Keycloak.
   * @param query The query string to search for users.
   * @returns A promise that resolves to the list of users matching the query.
   */
  async findUsersByAttribute(query: string): Promise<any> {
    return this.kcAdminClient.users.find({ q: query });
  }

  /**
   * Override the realm configuration for all subsequent requests.
   * @param realmName The new realm name.
   */
  setRealm(realmName: string): void {
    this.kcAdminClient.setConfig({
      realmName: realmName,
    });
  }

  /**
   * Fetch a list of groups from Keycloak.
   * @returns A promise that resolves to the list of groups.
   */
  async getGroups(): Promise<any> {
    return this.kcAdminClient.groups.find();
  }

  /**
   * Create a user in a specified realm.
   * @param realm The realm name.
   * @param user The user object to be created.
   * @returns A promise that resolves when the user is created.
   */
  async createUser(realm: string, user: any): Promise<any> {
    return this.kcAdminClient.users.create({
      realm: realm,
      ...user,
    });
  }

  get kCAdminClient() {
    return this.kcAdminClient;
  }

  getKcAccessToken() {
    return (
      // this.kcAdminClient.accessToken ||
      this.storageService.getData('accessToken')
    );
  }

  get isAuthenticated() {
    return this.getKcAccessToken() ? true : false;
  }

  logout() {
    this.storageService.clearState();
    this.router.navigate(['/']);
  }

  // ── Session expiry ──────────────────────────────────────────────
  // isAuthenticated only checks that a token is stored. These read the JWT's
  // `exp` as well, so the guards can send an expired session back to sign-in
  // before the shell renders.

  /** The stored access token, or null. Never throws (SSR has no storage). */
  private readStoredToken(): string | null {
    try {
      const token = this.getKcAccessToken();
      return typeof token === 'string' && token ? token : null;
    } catch {
      return null;
    }
  }

  /**
   * Measure the clock offset from a token Keycloak has just issued: its `iat`
   * is the server's "now". A device clock that runs fast would otherwise see a
   * fresh token as already expired and bounce straight back to sign-in.
   */
  private recordClockSkew(token: string | undefined): void {
    let skew = 0;
    try {
      const { iat } = jwtDecode<{ iat?: number }>(token ?? '');
      if (typeof iat === 'number') skew = Date.now() - iat * 1000;
    } catch {
      skew = 0;
    }
    this.storageService.saveSecureData(CLOCK_SKEW_KEY, skew);
  }

  /** The offset recorded at sign-in, in ms; 0 when unknown. */
  private get clockSkewMs(): number {
    try {
      const skew = this.storageService.getData(CLOCK_SKEW_KEY);
      return typeof skew === 'number' && Number.isFinite(skew) ? skew : 0;
    } catch {
      return 0;
    }
  }

  /**
   * When the stored token expires, in ms since the epoch on this device's
   * clock (corrected for skew); null if unknown.
   */
  get sessionExpiresAt(): number | null {
    const token = this.readStoredToken();
    if (!token) return null;
    try {
      const { exp } = jwtDecode<{ exp?: number }>(token);
      return typeof exp === 'number' ? exp * 1000 + this.clockSkewMs : null;
    } catch {
      return null;
    }
  }

  /** A token is stored and has not expired. */
  get hasValidSession(): boolean {
    if (!this.readStoredToken()) return false;
    const expiresAt = this.sessionExpiresAt;
    // A token without a readable expiry is trusted as before (presence only).
    return expiresAt === null || expiresAt - EXPIRY_SLACK_MS > Date.now();
  }

  /** A token is stored but its expiry has passed. */
  get isSessionExpired(): boolean {
    if (!this.readStoredToken()) return false;
    const expiresAt = this.sessionExpiresAt;
    return expiresAt !== null && expiresAt - EXPIRY_SLACK_MS <= Date.now();
  }

  /**
   * End a session the user did not end themselves (expired or rejected
   * token): clear it and, from inside the console, go to sign-in with a
   * notice and a way back to the page they were on. Public pages (reset
   * password, new password) stay where they are; their next request simply
   * goes out without the stale token.
   */
  endSession(
    reason: SignInReason = 'expired',
    returnUrl: string = this.router.url
  ): void {
    this.storageService.clearState();
    if (!isPlatformBrowser(this.platformId)) return;

    const path = this.router.url.split(/[?#]/)[0];
    if (!/^\/organization(?:\/|$)/.test(path)) return;

    const queryParams: Record<string, string> = {
      [SIGN_IN_REASON_PARAM]: reason,
    };
    const back = returnUrlParam(returnUrl);
    if (back) queryParams[RETURN_URL_PARAM] = back;
    this.router.navigate(['/'], { queryParams });
  }
}
